import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-white/80 text-lg">D.C. Technology Corp. Marketplace Platform</p>
            <p className="text-white/60 text-sm mt-2">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="space-y-8 text-white/90 leading-relaxed">
            {/* Section 1: Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                1. Introduction
              </h2>
              <p className="mb-4">
                D.C. Technology Corp. ("Company," "we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our marketplace platform ("Platform").
              </p>
              <p>
                By accessing or using our Platform, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access the Platform.
              </p>
            </section>

            {/* Section 2: Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                2. Information We Collect
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">2.1 Personal Information</h3>
                  <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4 mb-4">
                    <p className="text-blue-200 font-medium mb-2">We collect personal information that you provide directly to us:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-200 text-sm">
                      <li>Name, email address, phone number</li>
                      <li>Business information (company name, trade specialization, location)</li>
                      <li>Government-issued identification documents</li>
                      <li>Professional licenses and certifications</li>
                      <li>Payment information and financial data</li>
                      <li>Profile photos and service images</li>
                      <li>Communication records and messages</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">2.2 Automatically Collected Information</h3>
                  <p className="mb-2">We automatically collect certain information when you use our Platform:</p>
                  <ul className="list-disc list-inside space-y-1 text-white/85">
                    <li>Device information (IP address, browser type, operating system)</li>
                    <li>Usage data (pages visited, time spent, click patterns)</li>
                    <li>Location information (if permitted by your device settings)</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">2.3 Information from Third Parties</h3>
                  <p>We may receive information about you from third parties, including:</p>
                  <ul className="list-disc list-inside space-y-1 text-white/85">
                    <li>Payment processors (Stripe) for transaction processing</li>
                    <li>Identity verification services</li>
                    <li>Background check providers (for service providers)</li>
                    <li>Social media platforms (if you choose to connect)</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3: How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                3. How We Use Your Information
              </h2>
              
              <div className="space-y-4">
                <p>We use the information we collect for the following purposes:</p>
                
                <div className="bg-green-600/20 border border-green-400/30 rounded-lg p-4">
                  <h4 className="text-green-300 font-semibold mb-2">Platform Operations</h4>
                  <ul className="list-disc list-inside space-y-1 text-green-200 text-sm">
                    <li>Provide and maintain our marketplace services</li>
                    <li>Process transactions and manage payments</li>
                    <li>Facilitate communication between customers and providers</li>
                    <li>Verify identity and professional credentials</li>
                    <li>Enable job posting and bidding functionality</li>
                  </ul>
                </div>

                <div className="bg-yellow-600/20 border border-yellow-400/30 rounded-lg p-4">
                  <h4 className="text-yellow-300 font-semibold mb-2">Communication & Support</h4>
                  <ul className="list-disc list-inside space-y-1 text-yellow-200 text-sm">
                    <li>Send transactional emails and notifications</li>
                    <li>Provide customer support and respond to inquiries</li>
                    <li>Send marketing communications (with your consent)</li>
                    <li>Notify you of platform updates and changes</li>
                  </ul>
                </div>

                <div className="bg-purple-600/20 border border-purple-400/30 rounded-lg p-4">
                  <h4 className="text-purple-300 font-semibold mb-2">Security & Compliance</h4>
                  <ul className="list-disc list-inside space-y-1 text-purple-200 text-sm">
                    <li>Prevent fraud and ensure platform security</li>
                    <li>Comply with legal obligations and regulations</li>
                    <li>Conduct background checks for service providers</li>
                    <li>Monitor and investigate suspicious activities</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4: Data Security and Encryption */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                4. Data Security and Encryption
              </h2>
              
              <div className="bg-red-600/20 border border-red-400/30 rounded-lg p-6 mb-4">
                <h3 className="text-red-300 font-semibold mb-3">🔐 Advanced Security Measures</h3>
                <p className="text-red-200 mb-4">We implement industry-leading security measures to protect your personal information:</p>
                
                <div className="space-y-3">
                  <div className="bg-white/5 p-3 rounded">
                    <h4 className="text-white font-medium mb-1">AES-256 Encryption</h4>
                    <p className="text-red-200/90 text-sm">All sensitive data is encrypted using Advanced Encryption Standard with 256-bit keys</p>
                  </div>
                  
                  <div className="bg-white/5 p-3 rounded">
                    <h4 className="text-white font-medium mb-1">Multi-Level Security</h4>
                    <p className="text-red-200/90 text-sm">Different security levels applied based on data sensitivity:
                      <br />• ID Documents: MAXIMUM security (1M iterations, 64-byte salt)
                      <br />• Payment Information: MAXIMUM security
                      <br />• Personal Data: HIGH security (500K iterations, 32-byte salt)
                      <br />• Communications: HIGH security
                    </p>
                  </div>
                  
                  <div className="bg-white/5 p-3 rounded">
                    <h4 className="text-white font-medium mb-1">Key Management</h4>
                    <p className="text-red-200/90 text-sm">Advanced key management with automatic rotation, usage tracking, and secure storage</p>
                  </div>
                </div>
              </div>

              <p className="text-white/80">
                Additional security measures include secure data transmission (HTTPS/TLS), regular security audits, 
                access controls, and employee training on data protection best practices.
              </p>
            </section>

            {/* Section 5: Information Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                5. Information Sharing and Disclosure
              </h2>
              
              <div className="space-y-4">
                <p>We may share your information in the following circumstances:</p>
                
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">5.1 Service Providers</h3>
                  <p className="mb-2">We share information with trusted third-party service providers who assist us in:</p>
                  <ul className="list-disc list-inside space-y-1 text-white/85">
                    <li>Payment processing (Stripe, PayPal)</li>
                    <li>Email communications (SendGrid)</li>
                    <li>Identity verification services</li>
                    <li>Cloud hosting and data storage</li>
                    <li>Analytics and performance monitoring</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">5.2 Platform Users</h3>
                  <p>Limited information is shared between platform users to facilitate services:</p>
                  <ul className="list-disc list-inside space-y-1 text-white/85">
                    <li>Service provider profiles visible to customers</li>
                    <li>Customer contact information shared with accepted service providers</li>
                    <li>Reviews and ratings (without personal identifiers)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">5.3 Legal Requirements</h3>
                  <p>We may disclose information when required by law or to:</p>
                  <ul className="list-disc list-inside space-y-1 text-white/85">
                    <li>Comply with legal processes or government requests</li>
                    <li>Protect our rights, property, or safety</li>
                    <li>Prevent fraud or illegal activities</li>
                    <li>Enforce our Terms and Conditions</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 6: Your Rights and Choices */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                6. Your Rights and Choices
              </h2>
              
              <div className="space-y-4">
                <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4">
                  <h3 className="text-blue-300 font-semibold mb-2">Data Subject Rights</h3>
                  <p className="text-blue-200 mb-2">You have the following rights regarding your personal information:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-200 text-sm">
                    <li><strong>Access:</strong> Request copies of your personal data</li>
                    <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
                    <li><strong>Erasure:</strong> Request deletion of your personal data (subject to legal requirements)</li>
                    <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                    <li><strong>Restriction:</strong> Limit how we process your information</li>
                    <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">How to Exercise Your Rights</h3>
                  <p className="mb-2">To exercise these rights, contact us at:</p>
                  <div className="bg-white/5 p-3 rounded">
                    <p className="text-white/90">
                      Email: <a href="mailto:Corporate.dccorp@gmail.com" className="text-blue-400  underline">Corporate.dccorp@gmail.com</a>
                      <br />Subject: "Privacy Rights Request"
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 7: Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                7. Data Retention
              </h2>
              <p className="mb-4">
                We retain your personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
              <div className="bg-gray-600/20 border border-gray-400/30 rounded-lg p-4">
                <h4 className="text-gray-200 font-medium mb-2">Retention Periods:</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
                  <li>Account information: Duration of account + 3 years</li>
                  <li>Transaction records: 7 years (for tax and legal compliance)</li>
                  <li>Communication records: 2 years after account closure</li>
                  <li>Identity verification documents: 5 years (regulatory requirement)</li>
                </ul>
              </div>
            </section>

            {/* Section 8: Cookies and Tracking */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                8. Cookies and Tracking Technologies
              </h2>
              <p className="mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our Platform. 
                Cookies are small data files stored on your device that help us provide and improve our services.
              </p>
              <div className="space-y-3">
                <div className="bg-white/5 p-3 rounded">
                  <h4 className="text-white font-medium mb-1">Essential Cookies</h4>
                  <p className="text-white/80 text-sm">Required for basic platform functionality and security</p>
                </div>
                <div className="bg-white/5 p-3 rounded">
                  <h4 className="text-white font-medium mb-1">Performance Cookies</h4>
                  <p className="text-white/80 text-sm">Help us analyze platform usage and improve performance</p>
                </div>
                <div className="bg-white/5 p-3 rounded">
                  <h4 className="text-white font-medium mb-1">Functional Cookies</h4>
                  <p className="text-white/80 text-sm">Remember your preferences and personalize your experience</p>
                </div>
              </div>
            </section>

            {/* Section 9: International Transfers */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                9. International Data Transfers
              </h2>
              <p className="mb-4">
                Your information may be transferred to and processed in countries other than your country of residence. 
                We ensure appropriate safeguards are in place to protect your personal information when it is transferred internationally.
              </p>
              <p>
                By using our Platform, you consent to the transfer of your information to the United States and other countries 
                where we operate, which may have data protection laws that differ from those in your jurisdiction.
              </p>
            </section>

            {/* Section 10: Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                10. Children's Privacy
              </h2>
              <div className="bg-orange-600/20 border border-orange-400/30 rounded-lg p-4">
                <p className="text-orange-200">
                  <strong>⚠️ Important:</strong> Our Platform is not intended for individuals under the age of 18. 
                  We do not knowingly collect personal information from children under 18. If you become aware that 
                  a child has provided us with personal information, please contact us immediately.
                </p>
              </div>
            </section>

            {/* Section 11: Changes to Privacy Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                11. Changes to This Privacy Policy
              </h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, 
                legal requirements, or other factors. We will notify you of material changes by:
              </p>
              <ul className="list-disc list-inside space-y-1 text-white/85 mb-4">
                <li>Posting the updated Privacy Policy on our Platform</li>
                <li>Sending email notifications to registered users</li>
                <li>Displaying prominent notices on the Platform</li>
              </ul>
              <p>
                Your continued use of the Platform after any changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            {/* Section 12: Contact Information */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                12. Contact Us
              </h2>
              <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4">
                <p className="text-blue-200 mb-2">
                  <strong>If you have questions or concerns about this Privacy Policy, please contact us:</strong>
                </p>
                <div className="text-blue-200 text-sm space-y-1">
                  <p>D.C. Technology Corp.</p>
                  <p>Privacy Department</p>
                  <p>Email: <a href="mailto:Corporate.dccorp@gmail.com" className="text-blue-300  underline">Corporate.dccorp@gmail.com</a></p>
                  <p>Subject Line: "Privacy Policy Inquiry"</p>
                  <p>Phone: 1-800-DC-TECH (1-800-328-3244)</p>
                  <p>Address: [Company Address]</p>
                </div>
              </div>
            </section>

            {/* Effective Date */}
            <section className="border-t border-white/20 pt-6">
              <p className="text-center text-white/80 font-semibold">
                This Privacy Policy is effective as of {new Date().toLocaleDateString()} and remains in 
                effect until modified or replaced by D.C. Technology Corp.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;